<template>
  <div class="help-videos-modal" v-if="showModal">
    <div class="help-videos-content" ref="helpVideosContent">
      <div class="help-videos-header" @mousedown="startHelpVideosDrag">
        <h2>Help Videos</h2>
        <button class="close-button" @mousedown.stop @click="closeModal">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div v-if="!selectedVideo" class="help-videos-list-container">
        <div class="help-videos-search-bar">
          <div class="search-input-container">
            <i class="fas fa-search search-icon"></i>
            <input
              type="text"
              v-model="searchQuery"
              placeholder="Search videos by title or description..."
              class="search-input"
            />
            <button
              v-if="searchQuery"
              class="clear-search-button"
              @click="searchQuery = ''"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
          <button
            v-if="isAdmin"
            class="action-button"
            @click="showAddVideoModal"
          >
            <i class="fas fa-plus"></i> Add New Video
          </button>
        </div>

        <div class="help-videos-list">
          <div
            v-for="video in filteredVideos"
            :key="video.id"
            class="help-video-item"
          >
            <div class="play-button" @click="selectVideo(video)">
              <i class="fas fa-play-circle"></i>
            </div>
            <div class="help-video-info" @click="selectVideo(video)">
              <div
                class="help-video-title"
                v-html="highlightText(video.title)"
              ></div>
              <div
                class="help-video-summary"
                v-html="
                  highlightText(video.summary || 'No description available')
                "
              ></div>
            </div>
            <div class="help-video-actions" v-if="isAdmin">
              <button
                class="edit-button"
                @click.stop="showEditVideoModal(video)"
              >
                <i class="fas fa-edit"></i>
              </button>
              <button class="delete-button" @click.stop="deleteVideo(video.id)">
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>
          <div v-if="filteredVideos.length === 0" class="no-videos-message">
            {{
              searchQuery
                ? 'No videos match your search.'
                : 'No help videos available.'
            }}
          </div>
        </div>
      </div>

      <div v-else class="video-player-container">
        <div class="video-title">{{ selectedVideo.title }}</div>
        <div class="video-summary">
          {{ selectedVideo.summary || 'No description available' }}
        </div>
        <div class="video-player">
          <div
            v-if="videoLoading && !isExternalUrl"
            class="video-loading-spinner"
          >
            <div class="spinner"></div>
            <div class="loading-text">Loading video...</div>
            <div class="progress-bar-container" v-if="downloadProgress > 0">
              <div
                class="progress-bar"
                :style="{ width: `${downloadProgress}%` }"
              ></div>
              <div class="progress-text">
                {{ downloadProgress }}% downloaded
              </div>
            </div>
          </div>
          <!-- Embeddable URL (YouTube, Vimeo, etc.) -->
          <iframe
            v-if="isEmbeddableUrl"
            ref="videoIframe"
            :src="selectedVideo.videoFilename"
            :key="'iframe-' + videoPlayerKey"
            frameborder="0"
            allowfullscreen
            allow="autoplay; fullscreen"
          ></iframe>
          <!-- Direct video file URL or local file -->
          <video
            v-else
            ref="videoPlayer"
            controls
            :key="'video-' + videoPlayerKey"
            @error="handleVideoError"
            @canplay="videoLoading = false"
            :class="{ hidden: videoLoading }"
          ></video>
        </div>
        <div class="video-controls">
          <button class="back-button" @click="backToList">
            <i class="fas fa-arrow-left"></i> Back to List
          </button>
        </div>
      </div>
    </div>

    <!-- Add/Edit Video Modal -->
    <div class="video-form-modal" v-if="showVideoForm">
      <div class="video-form-content">
        <div class="video-form-header">
          <h3>{{ isEditing ? 'Edit' : 'Add' }} Video</h3>
          <button class="close-button" @click="showVideoForm = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="video-form">
          <div class="form-group">
            <label for="videoFilename">Video Filename or URL</label>
            <input
              type="text"
              id="videoFilename"
              v-model="videoForm.videoFilename"
              placeholder="example.mp4 or https://example.com/video"
            />
            <small
              style="
                color: #666;
                font-size: 0.85em;
                display: block;
                margin-top: 5px;
              "
            >
              Enter a local filename (e.g., video.mp4) or a full URL (e.g.,
              https://example.com/video)
            </small>
          </div>
          <div class="form-group">
            <label for="videoTitle">Title</label>
            <input
              type="text"
              id="videoTitle"
              v-model="videoForm.title"
              placeholder="Video Title"
            />
          </div>
          <div class="form-group">
            <label for="videoSummary">Summary</label>
            <textarea
              id="videoSummary"
              v-model="videoForm.summary"
              placeholder="Brief description of the video"
              rows="4"
            ></textarea>
          </div>
          <div class="video-form-actions">
            <button
              class="save-button"
              @click="saveVideo"
              :disabled="isSubmitting"
            >
              {{ isSubmitting ? 'Saving...' : 'Save' }}
            </button>
            <button
              class="cancel-button"
              @click="showVideoForm = false"
              :disabled="isSubmitting"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Custom Alert Popup -->
    <div class="custom-alert" v-if="showAlert">
      <div class="custom-alert-content">
        <div class="custom-alert-header">
          <i :class="alertIcon"></i>
          <span>{{ alertTitle }}</span>
        </div>
        <div class="custom-alert-body">
          {{ alertMessage }}
        </div>
        <div class="custom-alert-footer">
          <button class="alert-button" @click="closeAlert">OK</button>
        </div>
      </div>
    </div>

    <!-- Custom Confirmation Popup -->
    <div class="custom-confirm" v-if="showCustomConfirm">
      <div class="custom-confirm-content">
        <div class="custom-confirm-header">
          <i class="fas fa-question-circle"></i>
          <span>Confirm Action</span>
        </div>
        <div class="custom-confirm-body">
          Are you sure you want to delete the video "{{ confirmData.title }}"?
        </div>
        <div class="custom-confirm-footer">
          <button class="confirm-button" @click="confirmDeleteAction">
            Yes
          </button>
          <button class="cancel-button" @click="cancelConfirmAction">No</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';

// Create a simple client-side cache for videos
const videoCache = {
  // Check if a video exists in IndexedDB
  async hasVideo(filename) {
    return new Promise((resolve) => {
      if (!('indexedDB' in window)) {
        resolve(false);
        return;
      }

      const request = indexedDB.open('HelpVideosCache', 1);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('videos')) {
          db.createObjectStore('videos', { keyPath: 'filename' });
        }
      };

      request.onerror = () => resolve(false);

      request.onsuccess = (event) => {
        const db = event.target.result;
        try {
          const transaction = db.transaction(['videos'], 'readonly');
          const store = transaction.objectStore('videos');
          const getRequest = store.get(filename);

          getRequest.onsuccess = () => {
            resolve(!!getRequest.result);
          };

          getRequest.onerror = () => resolve(false);
        } catch (error) {
          console.error('Error checking video cache:', error);
          resolve(false);
        }
      };
    });
  },

  // Get a video from IndexedDB
  async getVideo(filename) {
    return new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        reject(new Error('IndexedDB not supported'));
        return;
      }

      const request = indexedDB.open('HelpVideosCache', 1);

      request.onerror = () => reject(new Error('Failed to open database'));

      request.onsuccess = (event) => {
        const db = event.target.result;
        try {
          const transaction = db.transaction(['videos'], 'readonly');
          const store = transaction.objectStore('videos');
          const getRequest = store.get(filename);

          getRequest.onsuccess = () => {
            if (getRequest.result) {
              resolve(getRequest.result.blob);
            } else {
              reject(new Error('Video not found in cache'));
            }
          };

          getRequest.onerror = () =>
            reject(new Error('Failed to get video from cache'));
        } catch (error) {
          console.error('Error retrieving from cache:', error);
          reject(error);
        }
      };
    });
  },

  // Save a video to IndexedDB
  async saveVideo(filename, blob) {
    return new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        reject(new Error('IndexedDB not supported'));
        return;
      }

      const request = indexedDB.open('HelpVideosCache', 1);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('videos')) {
          db.createObjectStore('videos', { keyPath: 'filename' });
        }
      };

      request.onerror = () => reject(new Error('Failed to open database'));

      request.onsuccess = (event) => {
        const db = event.target.result;
        try {
          const transaction = db.transaction(['videos'], 'readwrite');
          const store = transaction.objectStore('videos');

          // Store with timestamp for potential future expiration logic
          const saveRequest = store.put({
            filename,
            blob,
            timestamp: Date.now(),
          });

          saveRequest.onsuccess = () => resolve();
          saveRequest.onerror = () =>
            reject(new Error('Failed to save video to cache'));
        } catch (error) {
          console.error('Error saving to cache:', error);
          reject(error);
        }
      };
    });
  },
};

export default {
  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      videos: [],
      selectedVideo: null,
      searchQuery: '',
      isAdmin: false,
      userRole: null,
      showVideoForm: false,
      isEditing: false,
      videoForm: {
        id: null,
        videoFilename: '',
        title: '',
        summary: '',
      },
      isSubmitting: false,
      videoPlayerKey: 0,
      videoLoading: true,
      downloadProgress: 0,
      showAlert: false,
      alertTitle: '',
      alertMessage: '',
      alertIcon: '',
      showCustomConfirm: false,
      confirmData: {
        id: null,
        title: '',
        confirmed: false,
        action: '',
      },
      helpVideosDragState: null,
    };
  },
  computed: {
    filteredVideos() {
      if (!this.searchQuery || this.searchQuery.trim() === '') {
        return this.videos;
      }
      const query = this.searchQuery.toLowerCase().trim();
      return this.videos.filter((video) => {
        const title = (video.title || '').toLowerCase();
        const summary = (video.summary || '').toLowerCase();
        return title.includes(query) || summary.includes(query);
      });
    },
    isExternalUrl() {
      if (!this.selectedVideo || !this.selectedVideo.videoFilename) {
        return false;
      }
      const filename = this.selectedVideo.videoFilename.toLowerCase();
      return filename.startsWith('http://') || filename.startsWith('https://');
    },
    isDirectVideoUrl() {
      if (!this.isExternalUrl) return false;
      const filename = this.selectedVideo.videoFilename.toLowerCase();
      // Check if it's a direct video file URL (ends with video extension)
      return /\.(mp4|webm|ogg|m4v|mov)$/i.test(filename);
    },
    isEmbeddableUrl() {
      // For iframe embed (YouTube, Vimeo, etc.)
      return this.isExternalUrl && !this.isDirectVideoUrl;
    },
  },
  watch: {
    showModal(newVal) {
      if (newVal) {
        this.fetchVideos();
        this.fetchUserInfo();
        this.$nextTick(() => this.resetHelpVideosPosition());
      } else {
        this.selectedVideo = null;
        this.stopHelpVideosDrag();
      }
    },
  },
  beforeUnmount() {
    this.stopHelpVideosDrag();
  },
  methods: {
    resetHelpVideosPosition() {
      const el = this.$refs.helpVideosContent;
      if (!el) {
        return;
      }
      el.style.left = '';
      el.style.top = '';
      el.style.transform = '';
    },

    stopHelpVideosDrag() {
      document.removeEventListener('mousemove', this.onHelpVideosDrag);
      document.removeEventListener('mouseup', this.stopHelpVideosDrag);
      this.helpVideosDragState = null;
    },

    startHelpVideosDrag(event) {
      if (event.button !== 0 || event.target.closest('.close-button')) {
        return;
      }

      const el = this.$refs.helpVideosContent;
      if (!el) {
        return;
      }

      event.preventDefault();

      const rect = el.getBoundingClientRect();
      el.style.transform = 'none';
      el.style.left = `${rect.left}px`;
      el.style.top = `${rect.top}px`;

      this.helpVideosDragState = {
        startX: event.clientX,
        startY: event.clientY,
        startLeft: rect.left,
        startTop: rect.top,
      };

      document.addEventListener('mousemove', this.onHelpVideosDrag);
      document.addEventListener('mouseup', this.stopHelpVideosDrag);
    },

    onHelpVideosDrag(event) {
      const el = this.$refs.helpVideosContent;
      const drag = this.helpVideosDragState;
      if (!el || !drag) {
        return;
      }

      event.preventDefault();
      el.style.left = `${drag.startLeft + (event.clientX - drag.startX)}px`;
      el.style.top = `${drag.startTop + (event.clientY - drag.startY)}px`;
    },
    highlightText(text) {
      if (!this.searchQuery || this.searchQuery.trim() === '' || !text) {
        return text;
      }
      const query = this.searchQuery.trim();
      // Escape special regex characters
      const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedQuery})`, 'gi');
      return text.replace(regex, '<span class="highlight">$1</span>');
    },
    async fetchVideos() {
      try {
        const response = await axios.get('/api/desktop-help-videos');
        this.videos = response.data;
      } catch (error) {
        console.error('Error fetching help videos:', error);
      }
    },
    async fetchUserInfo() {
      try {
        const response = await axios.get('/api/user-info');
        if (response.data) {
          this.userRole = response.data.role;
          this.isAdmin = response.data.role === 1;
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
        this.isAdmin = false;
      }
    },
    selectVideo(video) {
      this.selectedVideo = video;
      this.videoPlayerKey += 1; // Update key to reload video

      // Check if this is an external URL
      const filename = video.videoFilename.toLowerCase();
      const isUrl =
        filename.startsWith('http://') || filename.startsWith('https://');
      const isDirectVideo =
        isUrl && /\.(mp4|webm|ogg|m4v|mov)$/i.test(filename);

      if (isUrl && !isDirectVideo) {
        // For embeddable URLs (YouTube, Vimeo, etc.), iframe will handle it
        this.videoLoading = false;
        return;
      }

      if (isDirectVideo) {
        // For direct video URLs, set the src directly
        this.videoLoading = true;
        this.$nextTick(() => {
          if (this.$refs.videoPlayer) {
            this.$refs.videoPlayer.src = video.videoFilename;
            this.$refs.videoPlayer.load();
            this.videoLoading = false;
          }
        });
        return;
      }

      // For local files, proceed with loading from server
      this.videoLoading = true;

      // Add a slight delay to ensure the videoPlayer ref is available after DOM update
      this.$nextTick(async () => {
        if (this.$refs.videoPlayer) {
          // Check if the video is cached
          const isCached = await videoCache.hasVideo(video.videoFilename);
          if (isCached) {
            try {
              const cachedBlob = await videoCache.getVideo(video.videoFilename);
              const url = URL.createObjectURL(cachedBlob);
              this.$refs.videoPlayer.src = url;
              this.$refs.videoPlayer.play();

              // Clean up the blob URL when the video is done
              this.$refs.videoPlayer.onended = () => {
                URL.revokeObjectURL(url);
              };
              return;
            } catch (error) {
              console.error('Error loading cached video:', error);
            }
          }

          // Get the authentication token
          const token = localStorage.getItem('token');
          if (!token) {
            console.error('No authentication token found');
            this.showCustomAlert(
              'Error',
              'Authentication error. Please log in again.',
              'fas fa-exclamation-triangle',
            );
            return;
          }

          // Create a fetch request with authentication
          // Use the same base URL that axios is configured with
          const baseUrl = axios.defaults.baseURL;

          fetch(
            `${baseUrl}/api/desktop-help-videos/${video.videoFilename}/stream`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          )
            .then((response) => {
              if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
              }

              // Get content length for progress calculation
              const contentLength = response.headers.get('Content-Length');
              let receivedLength = 0;
              const chunks = [];

              // Create a reader from the response body stream
              const reader = response.body.getReader();

              // Function to read all chunks
              return new Promise((resolve, reject) => {
                const readChunk = () => {
                  reader
                    .read()
                    .then(({ done, value }) => {
                      if (done) {
                        // All chunks received, resolve with final blob
                        const blob = new Blob(chunks, { type: 'video/mp4' });
                        resolve(blob);
                        return;
                      }

                      // Update progress
                      receivedLength += value.length;
                      if (contentLength) {
                        this.downloadProgress = Math.round(
                          (receivedLength / contentLength) * 100,
                        );
                      }

                      // Store the chunk
                      chunks.push(value);

                      // Continue reading
                      readChunk();
                    })
                    .catch(reject);
                };

                readChunk();
              });
            })
            .then((blob) => {
              // Reset progress
              this.downloadProgress = 0;

              // Save the video to cache
              videoCache.saveVideo(video.videoFilename, blob).catch((err) => {
                console.warn('Failed to cache video:', err);
              });

              // Create a blob URL and set it as the video source
              const url = URL.createObjectURL(blob);
              this.$refs.videoPlayer.src = url;
              this.$refs.videoPlayer.play();

              // Clean up the blob URL when the video is done
              this.$refs.videoPlayer.onended = () => {
                URL.revokeObjectURL(url);
              };
            })
            .catch((error) => {
              this.downloadProgress = 0;
              console.error('Error loading video:', error);
              this.showCustomAlert(
                'Error',
                'Error loading video. Please try again later.',
                'fas fa-exclamation-circle',
              );
            });
        }
      });
    },
    backToList() {
      this.selectedVideo = null;
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause();
      }
    },
    closeModal() {
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause();
      }
      this.$emit('close');
    },
    showAddVideoModal() {
      this.isEditing = false;
      this.videoForm = {
        id: null,
        videoFilename: '',
        title: '',
        summary: '',
      };
      this.showVideoForm = true;
    },
    showEditVideoModal(video) {
      this.isEditing = true;
      this.videoForm = {
        id: video.id,
        videoFilename: video.videoFilename,
        title: video.title,
        summary: video.summary,
      };
      this.showVideoForm = true;
    },
    async saveVideo() {
      // Validate form
      if (!this.videoForm.videoFilename || !this.videoForm.title) {
        this.showCustomAlert(
          'Validation Error',
          'Please fill out required fields (filename and title)',
          'fas fa-exclamation-triangle',
        );
        return;
      }

      this.isSubmitting = true;

      try {
        let response;
        if (this.isEditing) {
          // Update existing video
          response = await axios.put(
            `/api/desktop-help-videos/${this.videoForm.id}`,
            this.videoForm,
          );

          // Find and update the video in the list
          const index = this.videos.findIndex(
            (v) => v.id === this.videoForm.id,
          );
          if (index !== -1) {
            this.videos[index] = {
              ...this.videos[index],
              ...this.videoForm,
            };
          }
        } else {
          // Add new video
          response = await axios.post(
            '/api/desktop-help-videos',
            this.videoForm,
          );

          // Add the new video to the list
          this.videos.push(response.data);
        }

        this.showVideoForm = false;
      } catch (error) {
        console.error('Error saving video:', error);
        this.showCustomAlert(
          'Error',
          'Error saving video. Please try again.',
          'fas fa-exclamation-circle',
        );
      } finally {
        this.isSubmitting = false;
      }
    },
    async deleteVideo(id) {
      // Find the video to get its title for the deletion confirmation
      const videoToDelete = this.videos.find((v) => v.id === id);
      if (!videoToDelete) return;

      this.showCustomConfirm = true;
      this.confirmData = {
        id: id,
        title: videoToDelete.title,
        confirmed: false,
        action: 'delete',
      };
    },

    async confirmDeleteAction() {
      this.showCustomConfirm = false;
      const id = this.confirmData.id;

      try {
        await axios.delete(`/api/desktop-help-videos/${id}`);
        // Remove the video from the list
        this.videos = this.videos.filter((v) => v.id !== id);
      } catch (error) {
        console.error('Error deleting video:', error);
        this.showCustomAlert(
          'Error',
          'Error deleting video. Please try again.',
          'fas fa-exclamation-circle',
        );
      }
    },

    cancelConfirmAction() {
      this.showCustomConfirm = false;
    },
    handleVideoError() {
      this.showCustomAlert(
        'Error',
        'Error loading video. Please try again later.',
        'fas fa-exclamation-circle',
      );
    },
    closeAlert() {
      this.showAlert = false;
    },
    showCustomAlert(title, message, icon) {
      this.alertTitle = title;
      this.alertMessage = message;
      this.alertIcon = icon;
      this.showAlert = true;
    },
  },
};
</script>

<style scoped>
.help-videos-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9999;
}

.help-videos-content {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: white;
  border-radius: 8px;
  width: min(92vw, 1000px);
  height: min(86vh, 760px);
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.help-videos-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background-color: #675769;
  color: white;
  cursor: move;
  user-select: none;
}

.help-videos-header h2 {
  margin: 0;
  font-size: 1.5em;
}

.close-button {
  background: none;
  border: none;
  color: white;
  font-size: 1.2em;
  cursor: pointer;
}

.help-videos-list-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.help-videos-search-bar {
  padding: 15px 20px;
  background-color: #f9f9f9;
  border-bottom: 1px solid #ddd;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
}

.search-input-container {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
}

.search-icon {
  position: absolute;
  left: 12px;
  color: #999;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 10px 40px 10px 35px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.95em;
  box-sizing: border-box;
}

.search-input:focus {
  outline: none;
  border-color: #947797;
}

.clear-search-button {
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.clear-search-button:hover {
  color: #675769;
}

:deep(.highlight) {
  background-color: #947797;
  color: white;
  font-weight: bold;
  padding: 2px 4px;
  border-radius: 2px;
}

.help-videos-action-bar {
  padding: 10px 20px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #ddd;
}

.action-button {
  background-color: #947797;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 15px;
  cursor: pointer;
  font-size: 0.9em;
}

.action-button:hover {
  background-color: #7e5f81;
}

.help-videos-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px 20px;
}

.help-video-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  transition: background-color 0.2s;
}

.help-video-item:hover {
  background-color: #f9f9f9;
}

.play-button {
  margin-right: 15px;
  color: #675769;
  font-size: 2em; /* Make it larger */
  cursor: pointer;
  transition:
    transform 0.2s,
    color 0.2s;
  min-width: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.play-button:hover {
  color: #947797;
  transform: scale(1.1);
}

.help-video-info {
  flex: 1;
}

.help-video-title {
  font-weight: bold;
  color: #333;
  margin-bottom: 5px;
}

.help-video-summary {
  color: #666;
  font-size: 0.9em;
}

.help-video-actions {
  display: flex;
  gap: 10px;
}

.edit-button,
.delete-button {
  background: none;
  border: none;
  font-size: 1.3em; /* Increased size from 1em to 1.3em */
  cursor: pointer;
  color: #675769; /* Changed to match the title background color */
  transition: transform 0.2s;
}

.edit-button:hover,
.delete-button:hover {
  transform: scale(1.1);
}

.no-videos-message {
  text-align: center;
  color: #999;
  padding: 20px;
}

.video-player-container {
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.video-title {
  font-size: 1.3em;
  font-weight: bold;
  margin-bottom: 5px;
}

.video-summary {
  color: #666;
  margin-bottom: 15px;
}

.video-player {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.video-player video {
  width: 100%;
  height: 100%;
  min-height: 500px;
  object-fit: contain;
  background-color: #000;
}

.video-player iframe {
  width: 100%;
  height: 100%;
  min-height: 500px;
}

.video-player .hidden {
  display: none;
}

.video-loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.video-loading-spinner .spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #ddd;
  border-top: 4px solid #675769;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.video-loading-spinner .loading-text {
  margin-top: 10px;
  color: #675769;
  font-size: 0.9em;
}

.progress-bar-container {
  margin-top: 15px;
  width: 200px;
  height: 12px;
  background-color: #f5f5f5;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #ddd;
}

.progress-bar {
  height: 100%;
  background-color: #947797;
  transition: width 0.3s ease;
}

.progress-text {
  margin-top: 8px;
  font-size: 0.9em;
  color: #675769;
  font-weight: 500;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.video-controls {
  margin-top: 15px;
  display: flex;
  justify-content: space-between;
}

.back-button {
  background-color: #675769;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 15px;
  cursor: pointer;
}

.back-button:hover {
  background-color: #564758;
}

/* Add/Edit Video Form Modal */
.video-form-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.video-form-content {
  background-color: white;
  border-radius: 8px;
  width: 500px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.video-form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background-color: #675769;
  color: white;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.video-form-header h3 {
  margin: 0;
}

.video-form {
  padding: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  color: #555;
}

.form-group input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-sizing: border-box;
}

.form-group textarea {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-sizing: border-box;
}

.video-form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.save-button,
.cancel-button {
  padding: 8px 15px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.save-button {
  background-color: #947797;
  color: white;
}

.save-button:hover:not(:disabled) {
  background-color: #7e5f81;
}

.cancel-button {
  background-color: #f5f5f5;
  color: #333;
}

.cancel-button:hover:not(:disabled) {
  background-color: #e5e5e5;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Custom Alert Popup */
.custom-alert {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10001;
}

.custom-alert-content {
  background-color: white;
  border-radius: 8px;
  width: 300px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  text-align: center;
}

.custom-alert-header {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  font-size: 1.2em;
  font-weight: bold;
  color: #675769;
  margin-bottom: 15px;
}

.custom-alert-body {
  font-size: 1em;
  color: #333;
  margin-bottom: 20px;
}

.custom-alert-footer {
  display: flex;
  justify-content: center;
}

.alert-button {
  background-color: #947797;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 15px;
  cursor: pointer;
}

.alert-button:hover {
  background-color: #7e5f81;
}

/* Custom Confirmation Popup */
.custom-confirm {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10002;
}

.custom-confirm-content {
  background-color: white;
  border-radius: 8px;
  width: 300px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  text-align: center;
}

.custom-confirm-header {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  font-size: 1.2em;
  font-weight: bold;
  color: #675769;
  margin-bottom: 15px;
}

.custom-confirm-body {
  font-size: 1em;
  color: #333;
  margin-bottom: 20px;
}

.custom-confirm-footer {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.confirm-button {
  background-color: #947797;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 15px;
  cursor: pointer;
}

.confirm-button:hover {
  background-color: #7e5f81;
}

.cancel-button {
  background-color: #f5f5f5;
  color: #333;
  border: none;
  border-radius: 4px;
  padding: 8px 15px;
  cursor: pointer;
}

.cancel-button:hover {
  background-color: #e5e5e5;
}
</style>
